import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import api from '../services/api';

interface ThemeColors {
  primary: string;
  primaryHover: string;
  primaryLight: string;
  primaryDark: string;
  secondary: string;
  secondaryHover: string;
  secondaryLight: string;
  secondaryDark: string;
  accent: string;
  accentHover: string;
  accentLight: string;
  accentDark: string;
  background: string;
  backgroundSecondary: string;
  backgroundTertiary: string;
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textInverse: string;
  border: string;
  borderLight: string;
  borderDark: string;
  success: string;
  warning: string;
  error: string;
  info: string;
  headerBackground: string;
  headerText: string;
  footerBackground: string;
  footerText: string;
}

interface ThemeTypography {
  fontFamily: {
    primary: string;
    heading: string;
    monospace: string;
  };
  fontSize: {
    [key: string]: string;
  };
  fontWeight: {
    [key: string]: string;
  };
}

interface ThemeLayout {
  borderRadius: {
    [key: string]: string;
  };
}

interface Theme {
  colors: ThemeColors;
  typography: ThemeTypography;
  layout: ThemeLayout;
}

interface ThemeContextType {
  theme: Theme | null;
  loading: boolean;
  refreshTheme: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchTheme = async () => {
    try {
      const response = await api.get('/theme');
      if (response.data.success && response.data.data) {
        const themeData = response.data.data;
        setTheme(themeData);
        applyThemeToDOM(themeData);
      }
    } catch (error) {
      console.error('Error fetching theme:', error);
      // Apply default theme on error
      applyDefaultTheme();
    } finally {
      setLoading(false);
    }
  };

  const applyThemeToDOM = (themeData: Theme) => {
    const root = document.documentElement;

    // Apply color variables
    if (themeData.colors) {
      Object.entries(themeData.colors).forEach(([key, value]) => {
        const cssVarName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
        root.style.setProperty(cssVarName, value);
      });
    }

    // Apply typography variables
    if (themeData.typography) {
      if (themeData.typography.fontFamily) {
        Object.entries(themeData.typography.fontFamily).forEach(([key, value]) => {
          const cssVarName = `--font-${key}`;
          root.style.setProperty(cssVarName, value);
        });
      }

      if (themeData.typography.fontSize) {
        Object.entries(themeData.typography.fontSize).forEach(([key, value]) => {
          const cssVarName = `--font-size-${key}`;
          root.style.setProperty(cssVarName, value);
        });
      }

      if (themeData.typography.fontWeight) {
        Object.entries(themeData.typography.fontWeight).forEach(([key, value]) => {
          const cssVarName = `--font-weight-${key}`;
          root.style.setProperty(cssVarName, value);
        });
      }
    }

    // Apply layout variables
    if (themeData.layout && themeData.layout.borderRadius) {
      Object.entries(themeData.layout.borderRadius).forEach(([key, value]) => {
        const cssVarName = `--border-radius-${key}`;
        root.style.setProperty(cssVarName, value);
      });
    }
  };

  const applyDefaultTheme = () => {
    const defaultTheme: Theme = {
      colors: {
        primary: '#3b82f6',
        primaryHover: '#2563eb',
        primaryLight: '#dbeafe',
        primaryDark: '#1e40af',
        secondary: '#8b5cf6',
        secondaryHover: '#7c3aed',
        secondaryLight: '#ede9fe',
        secondaryDark: '#6d28d9',
        accent: '#10b981',
        accentHover: '#059669',
        accentLight: '#d1fae5',
        accentDark: '#047857',
        background: '#ffffff',
        backgroundSecondary: '#f9fafb',
        backgroundTertiary: '#f3f4f6',
        textPrimary: '#111827',
        textSecondary: '#6b7280',
        textTertiary: '#9ca3af',
        textInverse: '#ffffff',
        border: '#e5e7eb',
        borderLight: '#f3f4f6',
        borderDark: '#d1d5db',
        success: '#10b981',
        warning: '#f59e0b',
        error: '#ef4444',
        info: '#3b82f6',
        headerBackground: '#1f2937',
        headerText: '#ffffff',
        footerBackground: '#111827',
        footerText: '#9ca3af'
      },
      typography: {
        fontFamily: {
          primary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          heading: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          monospace: "'Fira Code', 'Courier New', monospace"
        },
        fontSize: {
          xs: '0.75rem',
          sm: '0.875rem',
          base: '1rem',
          lg: '1.125rem',
          xl: '1.25rem',
          '2xl': '1.5rem',
          '3xl': '1.875rem',
          '4xl': '2.25rem',
          '5xl': '3rem',
          '6xl': '3.75rem'
        },
        fontWeight: {
          light: '300',
          normal: '400',
          medium: '500',
          semibold: '600',
          bold: '700',
          extrabold: '800'
        }
      },
      layout: {
        borderRadius: {
          none: '0',
          sm: '0.125rem',
          base: '0.25rem',
          md: '0.375rem',
          lg: '0.5rem',
          xl: '0.75rem',
          '2xl': '1rem',
          full: '9999px'
        }
      }
    };

    setTheme(defaultTheme);
    applyThemeToDOM(defaultTheme);
  };

  useEffect(() => {
    fetchTheme();
  }, []);

  const refreshTheme = async () => {
    setLoading(true);
    await fetchTheme();
  };

  return (
    <ThemeContext.Provider value={{ theme, loading, refreshTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
